import type { HttpErrorKind } from "@/shared/http/client";
import { i18n } from "./index";

/** The shape `shared/http/client` throws, read here without depending on the class. */
interface ServerRefusal {
	readonly kind?: HttpErrorKind;
	readonly payload?: { error?: string; message?: unknown; params?: { received?: unknown } };
}

/** The coded sentence, quoting the refused value when the server sent it ("QA17 C" is not a decimal, QA 17). */
function sentenceOf(code: string, received: unknown): string {
	const { t } = i18n.global;
	const sentence = t(`error.${code}`);
	return typeof received === "string"
		? t("error.received", { sentence, value: received })
		: sentence;
}

/**
 * A FAILURE, AS A SENTENCE THE PERSON CAN ACT ON.
 *
 * The one helper every screen uses to draw a refusal, so that "não deu para
 * salvar" is not written forty times in forty slightly different ways, in one
 * language.
 *
 * ── THE ORDER, AND WHY IT IS THIS ONE ─────────────────────────────────────
 *
 *   1. The server's CODE (`shared/i18n/errors`). It is the only thing that says
 *      what actually happened, and it is the contract — `LAST_OWNER` is not an
 *      accident somebody hits once, it is what a workspace with a single owner
 *      gets every single time.
 *   2. The caller's own sentence, for a code that belongs to one feature and has
 *      no business in a shared map.
 *   3. The transport kind — `network`, `timeout`, `forbidden` — which always has
 *      an answer, because a request that never reached a controller has no code.
 *
 * The server's own `message` is never shown. It is short English written for
 * whoever reads the log, and "phone must be E.164" inside a Portuguese panel is
 * worse than a general sentence honestly stated. The one exception is an
 * INVARIANT's: its message is the one the screen's author wrote for that exact
 * case, in the workspace's language — the generic sentence hid which rule broke.
 *
 * A plain function and not a composable on purpose: the callers that need it
 * most are a mutation's `onError` and a toast, neither of which is inside
 * `setup()`. It reads the locale through `t`, so a `computed` wrapped around it
 * still redraws in the new language the instant the panel switches.
 */
export function describeServerError(e: unknown, fallback?: string): string {
	const refusal = e as ServerRefusal | null;
	const { t, te } = i18n.global;

	const code = refusal?.payload?.error;
	const said = refusal?.payload?.message;
	if (code === "INVARIANT_VIOLATED" && typeof said === "string" && said.trim()) return said;
	if (code && te(`error.${code}`)) return sentenceOf(code, refusal?.payload?.params?.received);
	if (fallback) return fallback;

	const kind = refusal?.kind;
	if (kind && te(`errorKind.${kind}`)) return t(`errorKind.${kind}`);
	return t("state.error");
}

/**
 * UMA RECUSA GRAVADA EM TEXTO — `CODE: frase do banco` ou `CODE(campo): …`, como
 * o servidor a escreve no erro de um fluxo e no resumo de um botão. Vira a frase
 * da casa, aberta pelo campo quando ele veio ("Quantidade: Esse valor não é aceito
 * aqui."); `fieldName` dá o rótulo do campo pelo código. `null` sem código conhecido.
 * `CODE(campo="valor"): …` traz o valor recusado, em JSON, e a frase o cita.
 */
export function codedRefusal(
	message: string,
	fieldName?: (code: string) => string | undefined,
): string | null {
	const found = /^([A-Z][A-Z0-9_]*)(?:\(([a-z][a-z0-9_]*)(?:=("(?:[^"\\]|\\.)*"))?\))?: /.exec(
		message,
	);
	const code = found?.[1];
	// A frase que a própria função lançou para quem apertou, ou a do invariante que o
	// autor escreveu para esse caso — já na língua do workspace (E2E 01/11).
	if (code === "FUNCTION_SAID" || code === "INVARIANT_VIOLATED")
		return (
			message
				.slice(found?.[0].length)
				.replace(/ \(line \d+\)$/, "")
				.trim() || null
		);
	if (!code || !i18n.global.te(`error.${code}`)) return null;
	const sentence = sentenceOf(code, found?.[3] && JSON.parse(found[3]));
	const field = found?.[2];
	return field ? `${fieldName?.(field) ?? field}: ${sentence}` : sentence;
}

/** O que o servidor devolve ao rodar uma automação a pedido (`DispatchSummary`). */
export interface RunSummary {
	readonly executed: number;
	readonly failed: number;
	/** Dos executados, os que ainda correm — um fluxo parado numa espera. */
	readonly waiting?: number;
	readonly firstFailure?: { readonly reason: string };
}

/**
 * RODAR A PEDIDO, DITO COMO FOI — o botão de tela e o "Rodar agora" numa regra
 * só (QA 16): falhou com o porquê, começou e espera, nada a fazer, ou feito.
 * "Feito" com uma espera no meio mentia; "Disparada" escondia uma falha.
 */
export function describeRun(
	summary: RunSummary,
	name: string,
	fieldName?: (code: string) => string | undefined,
): { failed: boolean; text: string } {
	const { t } = i18n.global;
	if (summary.failed > 0) {
		const why = summary.firstFailure && codedRefusal(summary.firstFailure.reason, fieldName);
		return {
			failed: true,
			text: why ? t("run.failed", { name, reason: why }) : t("run.failedUnknown", { name }),
		};
	}
	if (summary.executed === 0) return { failed: false, text: t("run.nothing", { name }) };
	if (summary.waiting) return { failed: false, text: t("run.started", { name }) };
	return { failed: false, text: t("run.done", { name, n: summary.executed }, summary.executed) };
}
